import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Pagination } from "@mui/material";
import HorizontalProductCard from "../Cards/HorizontalProductCard";

const BestSeller = (props) => {
  const [bestSellers, setBestSellers] = useState([]);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 8,
    totalRecords: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const sectionRef = useRef(null);

  const handlePageChange = (event, value) => {
    setFilter((prev) => ({ ...prev, page: value }));
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const fetchBestSellers = () => {
    setLoading(true);
    if (error) setError(false);
    axios
      .get("get-products", {
        params: { bestsellers: true, ...filter },
      })
      .then((response) => {
        const data = response?.data?.data || [];
        setBestSellers(data.map((el) => ({ ...el, id: el._id })));
        setFilter((prev) => ({
          ...prev,
          totalRecords: response?.data?.totalRecords,
        }));
      })
      .catch(() => {
        setBestSellers([]);
        setError(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchBestSellers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.page]);

  const totalRecords = Number(filter?.totalRecords) || 0;
  const limit = Number(filter?.limit) || 8;
  const pageCount = Math.max(1, Math.ceil(totalRecords / limit));

  return (
    <section className="gpbs-section" ref={sectionRef}>
      <div className="gpbs-bg-orb gpbs-bg-orb-tr" />
      <div className="gpbs-bg-orb gpbs-bg-orb-bl" />
      <div className="gpbs-bg-grid" aria-hidden="true" />

      <div className="gpbs-inner">
        {/* HEADER */}
        <div className="gpbs-head">
          <div className="gpbs-head-text">
            <div className="gpbs-eyebrow">
              <span className="gpbs-eyebrow-dot" />
              CUSTOMER FAVOURITES · TOP RANKED
            </div>
            <h2 className="gpbs-title">
              Best <span className="gpbs-title-accent">Sellers</span>
            </h2>
            <p className="gpbs-sub">
              The most-purchased gift cards on GlobalGiftPass — loved by
              thousands of customers across the region.
            </p>
          </div>

          <div className="gpbs-head-right">
            {!loading && !error && totalRecords > 0 && (
              <div className="gpbs-meta-pill">
                <strong>{totalRecords}</strong> top picks
              </div>
            )}
            <Link to="/shop" className="gpbs-viewall">
              View all
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
          </div>
        </div>

        {/* GRID */}
        {loading ? (
          <div className="gpbs-grid">
            {Array.from({ length: filter?.limit || 8 }).map((_, i) => (
              <div key={i} className="gpbs-skel">
                <div className="gpbs-skel-img" />
                <div className="gpbs-skel-body">
                  <div className="gpbs-skel-line gpbs-skel-line-lg" />
                  <div className="gpbs-skel-line gpbs-skel-line-sm" />
                  <div className="gpbs-skel-line gpbs-skel-line-md" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="gpbs-empty">
            <div className="gpbs-empty-icon" aria-hidden="true">⚠</div>
            <h3>Couldn't load best sellers</h3>
            <p>Please check your connection and try again.</p>
            <button
              type="button"
              className="gpbs-empty-btn"
              onClick={fetchBestSellers}
            >
              Try again
            </button>
          </div>
        ) : bestSellers.length === 0 ? (
          <div className="gpbs-empty">
            <div className="gpbs-empty-icon" aria-hidden="true">🏆</div>
            <h3>No best sellers yet</h3>
            <p>Sales rankings update daily — check back soon.</p>
            <Link to="/shop" className="gpbs-empty-btn">
              Browse all gift cards
            </Link>
          </div>
        ) : (
          <>
            <div className="gpbs-grid">
              {bestSellers.map((product, index) => (
                <div
                  key={product?.id || index}
                  className="gpbs-card-wrap"
                  style={{ animationDelay: `${Math.min(index * 50, 480)}ms` }}
                >
                  {index < 3 && (
                    <span className={`gpbs-rank gpbs-rank-${index + 1}`}>
                      #{index + 1}
                    </span>
                  )}
                  <div className="gpbs-card-glow" aria-hidden="true" />
                  <HorizontalProductCard product={product} />
                </div>
              ))}
            </div>

            {pageCount > 1 && (
              <div className="gpbs-pagination">
                <Pagination
                  count={pageCount}
                  size="medium"
                  page={filter?.page || 1}
                  onChange={handlePageChange}
                  siblingCount={1}
                  shape="rounded"
                />
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        /* ============ SECTION ============ */
        .gpbs-section {
          position: relative;
          background: linear-gradient(180deg, #07060a 0%, #0b0b0c 100%);
          padding: 70px 22px 80px;
          overflow: hidden;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          color: #fff;
          border-top: 1px solid rgba(212, 168, 87, 0.08);
        }
        .gpbs-bg-orb {
          position: absolute;
          width: 380px;
          height: 380px;
          border-radius: 50%;
          filter: blur(120px);
          pointer-events: none;
          opacity: 0.55;
        }
        .gpbs-bg-orb-tr {
          background: rgba(212, 168, 87, 0.18);
          top: -140px;
          right: -100px;
        }
        .gpbs-bg-orb-bl {
          background: rgba(212, 168, 87, 0.12);
          bottom: -160px;
          left: -100px;
        }
        .gpbs-bg-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(212, 168, 87, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(212, 168, 87, 0.04) 1px, transparent 1px);
          background-size: 48px 48px;
          mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
          -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
          pointer-events: none;
          opacity: 0.5;
        }

        .gpbs-inner {
          position: relative;
          max-width: 1300px;
          margin: 0 auto;
          z-index: 1;
        }

        /* ============ HEADER ============ */
        .gpbs-head {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 18px;
          margin-bottom: 36px;
          flex-wrap: wrap;
        }
        .gpbs-head-text { max-width: 720px; }
        .gpbs-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px;
          background: rgba(212, 168, 87, 0.1);
          border: 1px solid rgba(212, 168, 87, 0.3);
          color: #d4a857;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 2px;
          margin-bottom: 14px;
        }
        .gpbs-eyebrow-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #d4a857;
          box-shadow: 0 0 10px #d4a857;
          animation: gpbs-pulse 1.6s ease-in-out infinite;
        }
        @keyframes gpbs-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(0.85); }
        }
        .gpbs-title {
          font-family: "Playfair Display", "Times New Roman", serif;
          font-size: 38px;
          font-weight: 700;
          color: #fff;
          margin: 0 0 8px;
          line-height: 1.15;
          letter-spacing: -0.4px;
        }
        .gpbs-title-accent {
          background: linear-gradient(135deg, #f1cf85, #b58a3f);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          font-style: italic;
          font-weight: 600;
        }
        .gpbs-sub {
          font-size: 14.5px;
          color: #9aa0a8;
          line-height: 1.55;
          margin: 0;
          max-width: 580px;
        }

        .gpbs-head-right {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        .gpbs-meta-pill {
          padding: 8px 14px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(212, 168, 87, 0.2);
          color: #cfd2d6;
          border-radius: 999px;
          font-size: 12px;
        }
        .gpbs-meta-pill strong { color: #d4a857; font-weight: 700; margin-right: 4px; }

        .gpbs-viewall {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 11px 18px;
          background: linear-gradient(135deg, #f1cf85 0%, #d4a857 50%, #b58a3f 100%);
          color: #1a1208 !important;
          border: 1px solid rgba(255, 232, 178, 0.5);
          border-radius: 999px;
          font-size: 12.5px;
          font-weight: 700;
          letter-spacing: 0.6px;
          text-decoration: none;
          box-shadow: 0 6px 16px rgba(212, 168, 87, 0.3);
          transition: transform .25s ease, box-shadow .25s ease, filter .25s ease;
        }
        .gpbs-viewall:hover {
          color: #1a1208 !important;
          text-decoration: none;
          transform: translateY(-1px);
          filter: brightness(1.05);
          box-shadow: 0 10px 22px rgba(212, 168, 87, 0.45);
        }
        .gpbs-viewall svg { transition: transform .2s ease; }
        .gpbs-viewall:hover svg { transform: translateX(2px); }

        /* ============ GRID (4-col on desktop, horizontal cards) ============ */
        .gpbs-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 18px;
        }
        .gpbs-card-wrap {
          position: relative;
          opacity: 0;
          transform: translateY(14px);
          animation: gpbs-fadeup .55s cubic-bezier(0.22, 1, 0.36, 1) forwards;
          transition: transform .35s cubic-bezier(0.22, 1, 0.36, 1);
          border-radius: 14px;
        }
        .gpbs-card-wrap:hover { transform: translateY(-5px); }
        @keyframes gpbs-fadeup {
          to { opacity: 1; transform: translateY(0); }
        }
        .gpbs-card-glow {
          position: absolute;
          inset: -2px;
          border-radius: 16px;
          background: linear-gradient(135deg, rgba(212, 168, 87, 0.55), rgba(180, 130, 40, 0));
          opacity: 0;
          transition: opacity .3s ease;
          pointer-events: none;
          z-index: 0;
          filter: blur(8px);
        }
        .gpbs-card-wrap:hover .gpbs-card-glow { opacity: 0.55; }

        /* ----- RANK BADGES (top 3 only) ----- */
        .gpbs-rank {
          position: absolute;
          top: 8px;
          right: 8px;
          z-index: 3;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.4px;
          padding: 4px 10px;
          border-radius: 999px;
          color: #1a1208;
          box-shadow: 0 6px 14px rgba(0, 0, 0, 0.4);
          border: 1px solid rgba(255, 245, 215, 0.5);
        }
        .gpbs-rank-1 { background: linear-gradient(135deg, #ffd66b, #d4a857); }
        .gpbs-rank-2 { background: linear-gradient(135deg, #e2e2e2, #b8b8b8); border-color: rgba(255,255,255,0.5); }
        .gpbs-rank-3 { background: linear-gradient(135deg, #e09d6e, #b06b3e); border-color: rgba(255, 200, 160, 0.45); }

        /* ----- DARK CARD STYLING (overrides on HorizontalProductCard) ----- */
        .gpbs-card-wrap .horizontal-product-item {
          position: relative;
          z-index: 1;
          display: flex !important;
          align-items: stretch;
          gap: 12px !important;
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%) !important;
          border: 1px solid rgba(212, 168, 87, 0.18) !important;
          border-radius: 14px !important;
          padding: 12px !important;
          box-shadow: 0 8px 22px rgba(0, 0, 0, 0.35) !important;
          color: #fff !important;
          text-decoration: none !important;
          transition: border-color .3s ease, box-shadow .3s ease !important;
          height: 100%;
          min-height: 130px;
        }
        .gpbs-card-wrap:hover .horizontal-product-item {
          border-color: rgba(212, 168, 87, 0.55) !important;
          box-shadow: 0 14px 32px rgba(212, 168, 87, 0.18), 0 6px 14px rgba(0, 0, 0, 0.4) !important;
        }
        .gpbs-card-wrap .horizontal-product-item .product-image {
          flex-shrink: 0 !important;
          width: 88px !important;
          height: 88px !important;
          background: #0b0b0c !important;
          border: 1px solid rgba(212, 168, 87, 0.15) !important;
          border-radius: 10px !important;
          overflow: hidden !important;
          padding: 0 !important;
          margin: 0 !important;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .gpbs-card-wrap .horizontal-product-item .product-image img {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
          transition: transform .5s cubic-bezier(0.22, 1, 0.36, 1) !important;
        }
        .gpbs-card-wrap:hover .horizontal-product-item .product-image img {
          transform: scale(1.05) !important;
        }
        .gpbs-card-wrap .horizontal-product-item .product-info {
          flex: 1 !important;
          min-width: 0 !important;
          display: flex !important;
          flex-direction: column !important;
          gap: 4px !important;
          padding: 2px 0 !important;
          margin: 0 !important;
        }
        .gpbs-card-wrap .horizontal-product-item .product-card-title {
          color: #fff !important;
          font-size: 13.5px !important;
          font-weight: 600 !important;
          line-height: 1.35 !important;
          margin: 0 !important;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .gpbs-card-wrap:hover .horizontal-product-item .product-card-title {
          color: #d4a857 !important;
        }
        .gpbs-card-wrap .horizontal-product-item .product-info > div {
          margin: 0 !important;
        }
        .gpbs-card-wrap .horizontal-product-item .product-info .flex {
          gap: 6px !important;
        }
        .gpbs-card-wrap .horizontal-product-item .product-info span {
          color: #9aa0a8;
          font-size: 11px;
        }
        .gpbs-card-wrap .horizontal-product-item .price {
          margin-top: auto !important;
          padding-top: 6px !important;
          border-top: 1px solid rgba(212, 168, 87, 0.12) !important;
        }
        .gpbs-card-wrap .horizontal-product-item .present-price {
          color: #d4a857 !important;
          font-weight: 700 !important;
          font-size: 14px !important;
        }

        /* ============ SKELETON ============ */
        .gpbs-skel {
          display: flex;
          gap: 12px;
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px solid rgba(212, 168, 87, 0.12);
          border-radius: 14px;
          padding: 12px;
          min-height: 130px;
        }
        .gpbs-skel-img {
          flex-shrink: 0;
          width: 88px;
          height: 88px;
          border-radius: 10px;
          background: linear-gradient(90deg, #1a1208 0%, #2a1f10 50%, #1a1208 100%);
          background-size: 200% 100%;
          animation: gpbs-shimmer 1.4s linear infinite;
        }
        .gpbs-skel-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 8px;
          justify-content: center;
        }
        .gpbs-skel-line {
          height: 11px;
          border-radius: 4px;
          background: linear-gradient(90deg, #1a1208 0%, #2a1f10 50%, #1a1208 100%);
          background-size: 200% 100%;
          animation: gpbs-shimmer 1.4s linear infinite;
        }
        .gpbs-skel-line-lg { height: 14px; width: 85%; }
        .gpbs-skel-line-md { width: 50%; }
        .gpbs-skel-line-sm { width: 35%; }
        @keyframes gpbs-shimmer {
          from { background-position: 200% 0; }
          to { background-position: -200% 0; }
        }

        /* ============ EMPTY / ERROR ============ */
        .gpbs-empty {
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px dashed rgba(212, 168, 87, 0.3);
          border-radius: 16px;
          padding: 56px 24px;
          text-align: center;
          max-width: 540px;
          margin: 0 auto;
        }
        .gpbs-empty-icon {
          font-size: 36px;
          color: #d4a857;
          margin-bottom: 12px;
        }
        .gpbs-empty h3 {
          font-size: 18px;
          color: #fff;
          margin: 0 0 6px;
          font-weight: 700;
        }
        .gpbs-empty p {
          color: #9aa0a8;
          margin: 0 0 20px;
          font-size: 13.5px;
        }
        .gpbs-empty-btn {
          display: inline-flex;
          align-items: center;
          background: linear-gradient(135deg, #d4a857, #b58a3f);
          color: #1a1208 !important;
          border: none;
          padding: 11px 22px;
          border-radius: 10px;
          font-weight: 700;
          font-size: 13px;
          letter-spacing: 0.5px;
          cursor: pointer;
          text-decoration: none;
          transition: transform .2s ease, filter .2s ease;
          font-family: inherit;
        }
        .gpbs-empty-btn:hover {
          transform: translateY(-2px);
          filter: brightness(1.05);
          color: #1a1208 !important;
          text-decoration: none;
        }

        /* ============ PAGINATION ============ */
        .gpbs-pagination {
          display: flex;
          justify-content: center;
          padding: 40px 0 0;
        }
        .gpbs-pagination .MuiPaginationItem-root {
          color: #cfd2d6 !important;
          font-weight: 600;
          border-radius: 10px;
          margin: 0 2px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(212, 168, 87, 0.15);
        }
        .gpbs-pagination .MuiPaginationItem-root:hover {
          background: rgba(212, 168, 87, 0.15) !important;
          color: #d4a857 !important;
          border-color: rgba(212, 168, 87, 0.4);
        }
        .gpbs-pagination .MuiPaginationItem-root.Mui-selected {
          background: linear-gradient(135deg, #d4a857, #b58a3f) !important;
          color: #1a1208 !important;
          border-color: transparent !important;
          box-shadow: 0 6px 14px rgba(212, 168, 87, 0.3);
        }
        .gpbs-pagination .MuiPaginationItem-root.Mui-selected:hover {
          background: linear-gradient(135deg, #f1cf85, #d4a857) !important;
        }

        /* ============ RESPONSIVE ============ */
        @media (max-width: 1100px) {
          .gpbs-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        @media (max-width: 820px) {
          .gpbs-section { padding: 56px 18px 64px; }
          .gpbs-title { font-size: 28px; }
          .gpbs-sub { font-size: 13.5px; }
          .gpbs-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
        }
        @media (max-width: 480px) {
          .gpbs-section { padding: 44px 14px 52px; }
          .gpbs-title { font-size: 22px; }
          .gpbs-eyebrow { font-size: 10px; letter-spacing: 1.5px; }
          .gpbs-grid { grid-template-columns: 1fr; }
          .gpbs-head { gap: 14px; flex-direction: column; align-items: flex-start; }
          .gpbs-head-right { width: 100%; }
          .gpbs-viewall { flex: 1; justify-content: center; }
          .gpbs-card-wrap .horizontal-product-item .product-image,
          .gpbs-skel-img { width: 80px !important; height: 80px !important; }
        }
      `}</style>
    </section>
  );
};

export default BestSeller;
